// Receipt AI Extraction Service
// Uses Claude API (Anthropic) to extract structured data from receipt images

const API_URL = 'https://api.anthropic.com/v1/messages';
const API_KEY = process.env.REACT_APP_ANTHROPIC_API_KEY;

// Debug: log whether key is loaded (without revealing the key)
console.log('[Ground Control] AI configured:', !!API_KEY && API_KEY !== 'your-new-api-key-here' ? 'YES' : 'NO — add key to .env and restart with npm start');

export async function extractReceiptData(base64Image, mediaType) {
  const isImage = mediaType.startsWith('image/');
  const isPdf = mediaType === 'application/pdf';

  if (!isImage && !isPdf) {
    throw new Error('Unsupported file type. Please upload an image or PDF.');
  }

  console.log('[Ground Control] Sending receipt to AI...', { mediaType, imageSize: Math.round(base64Image.length / 1024) + 'KB' });

  const content = [
    {
      type: isImage ? 'image' : 'document',
      source: {
        type: 'base64',
        media_type: mediaType,
        data: base64Image,
      },
    },
    {
      type: 'text',
      text: `You are a receipt data extraction assistant for a construction contracting business called Satellite Gardens.

Extract the following information from this receipt and return ONLY a valid JSON object (no markdown, no backticks, no explanation):

{
  "vendor": "Store/company name",
  "date": "YYYY-MM-DD",
  "total": 123.45,
  "subtotal": 123.45,
  "tax": 0.00,
  "items": [
    { "name": "item description", "amount": 12.99, "qty": 1 },
    { "name": "another item", "amount": 45.00, "qty": 2 }
  ],
  "suggestedDescription": "A brief 2-4 word description suitable for a construction ledger (e.g. 'framing lumber', 'plumbing supplies', 'tile materials', 'electrical supplies')",
  "suggestedCategory": "One of: Construction Materials, Vehicle/Fuel, Office Expense, Communications, Insurance/Bond, Meals (50%), Meals (100%), Advertising, Licensing, Business Travel, Legal and Professional Fees",
  "confidence": "high or medium or low"
}

Guidelines for items array:
- Each item must have "name" (string), "amount" (number — line total after quantity), and "qty" (number)
- "amount" is the TOTAL for that line (price × qty), not per-unit
- Include every line item you can read from the receipt
- If quantities are listed, include them; otherwise default qty to 1
- If you can't read individual prices, put all items in one entry with the subtotal as the amount

Guidelines for suggestedDescription:
- Use construction trade shorthand (like a contractor would write in their ledger)
- Group similar items: "2x4 lumber, screws, nails" → "framing materials"
- Keep it brief: 2-4 words max
- Common patterns: "tile supplies", "plumbing fixtures", "paint supplies", "hardware", "electrical supplies"

If you cannot read the receipt clearly, set confidence to "low" and fill in what you can.
Return ONLY the JSON object.`,
    },
  ];

  const response = await fetch(API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': API_KEY,
      'anthropic-version': '2023-06-01',
      'anthropic-dangerous-direct-browser-access': 'true',
    },
    body: JSON.stringify({
      model: 'claude-sonnet-4-20250514',
      max_tokens: 1000,
      messages: [{ role: 'user', content }],
    }),
  });

  if (!response.ok) {
    const errData = await response.json().catch(() => ({}));
    const errMsg = errData.error?.message || `API error: ${response.status}`;
    console.error('[Ground Control] AI API error:', errMsg);
    throw new Error(errMsg);
  }

  const data = await response.json();
  console.log('[Ground Control] AI response received:', data.content?.length, 'blocks');

  const text = data.content
    .filter(block => block.type === 'text')
    .map(block => block.text)
    .join('');

  // Parse JSON, stripping any markdown fences if present
  const cleaned = text.replace(/```json\s*/g, '').replace(/```\s*/g, '').trim();

  try {
    const parsed = JSON.parse(cleaned);
    console.log('[Ground Control] AI extracted:', parsed.vendor, parsed.total);

    return {
      vendor: parsed.vendor || '',
      date: parsed.date || '',
      total: parseFloat(parsed.total) || 0,
      subtotal: parseFloat(parsed.subtotal) || 0,
      tax: parseFloat(parsed.tax) || 0,
      items: Array.isArray(parsed.items) ? parsed.items.map(item => {
        // Handle both old string format and new object format
        if (typeof item === 'string') return { name: item, amount: 0, qty: 1 };
        return {
          name: item.name || '',
          amount: parseFloat(item.amount) || 0,
          qty: parseInt(item.qty) || 1,
        };
      }) : [],
      suggestedDescription: parsed.suggestedDescription || '',
      suggestedCategory: parsed.suggestedCategory || 'Construction Materials',
      confidence: parsed.confidence || 'low',
    };
  } catch (parseErr) {
    console.error('[Ground Control] Failed to parse AI response:', cleaned);
    throw new Error('AI returned invalid data. Falling back to manual entry.');
  }
}

export function isAIConfigured() {
  return !!API_KEY && API_KEY !== 'your-new-api-key-here';
}

// Returns a human-readable status for the upload screen
export function getAIStatus() {
  if (!API_KEY) return 'No API key found in .env — restart app after adding it';
  if (API_KEY === 'your-new-api-key-here') return 'Replace placeholder key in .env and restart';
  return 'ready';
}
